import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { BipTemplateService } from '../../bip-template.service';
import { CommonService } from '../../../../shared/services/common.service';
import { LoaderService } from '../../../../core/services/loader.service';
import {
  DrawerParams,
  FilterFormValues,
  OrgTemplate,
  TemplateUserResponse,
} from '../../bip-template.interface';
import { LookupItem } from '../../../../core/models/lookup.models';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { AuthService } from '../../../../core/services/auth.service';
import { OrganizationDetailModel } from '../../../organizations/organization.interface';

@Component({
  selector: 'app-template-logs-filter',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzSelectModule,
    NzDatePickerModule,
    SvgIconComponent,
    NzButtonModule,
    ReactiveFormsModule,
    NzSpinModule,
    CommonModule,
  ],
  templateUrl: './template-logs-filter.component.html',
  styleUrl: './template-logs-filter.component.scss',
})
export class TemplateLogsFilterComponent implements OnDestroy {
  templateLogsFilterForm!: FormGroup;
  orgId = 0;
  private readonly TOKEN_KEY = 'auth_token';
  userList: TemplateUserResponse[] = [];
  auditTypeList: LookupItem[] = [];
  templateList: OrgTemplate[] = [];
  orgData: OrganizationDetailModel | null = null;
  loading$: Observable<boolean> = of(false);
  private destroy$ = new Subject<void>();
  constructor(
    private fb: FormBuilder,
    private templateService: BipTemplateService,
    private commonService: CommonService,
    private loaderService: LoaderService,
    private drawerService: DrawerService,
    private authService: AuthService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.templateLogsFilterForm = this.fb.group({
      templateId: [null],
      dataAuditTypeId: [null],
      userId: [null],
      dateFilter: [null],
    });
    this.drawerService
      .getParams<DrawerParams>(DRAWER_IDS.TEMPLATE_LOGS_FILTER)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        this.orgId = params?.orgnizationId || 0;
        if (params?.appliedFilters) {
          this.templateLogsFilterForm.patchValue(params.appliedFilters);
        }
        this.getMasterData();
      });
  }

  async getMasterData() {
    this.loaderService.show();
    await Promise.all([
      this.templateService
        .getTemplateLogsLookup(Number(this.orgId))
        .toPromise(),
      this.commonService.getDataByType('DataAuditType').toPromise(),
      this.templateService
        .getBipTemplateByOrgId(Number(this.orgId))
        .toPromise(),
    ])
      .then(([lookupResponse, auditTypeList, templateList]) => {
        this.userList = lookupResponse?.result
          ? lookupResponse.result.templateUserReponses
          : [];
        this.auditTypeList = auditTypeList?.result ? auditTypeList.result : [];
        this.templateList = templateList?.result ? templateList.result : [];
      })
      .catch(e => console.error(e))
      .finally(() => this.loaderService.hide());
  }

  reset() {
    this.templateLogsFilterForm.reset();
    const filterValues = this.templateLogsFilterForm.value;
    const appliedFilterLength = this.calculateAppliedFilterLength(filterValues);
    this.drawerService.setPersistentState(DRAWER_IDS.TEMPLATE_LOGS_FILTER, {
      appliedFilters: filterValues,
      appliedFilterLength: appliedFilterLength,
    });
    const drawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.TEMPLATE_LOGS_FILTER
    );
    drawerRef?.close({ filterValues });
  }

  apply() {
    const filterValues = this.templateLogsFilterForm.value;
    if (filterValues.dateFilter) {
      filterValues.dateFilter = new Date(filterValues.dateFilter).toISOString();
    }
    const appliedFilterLength = this.calculateAppliedFilterLength(filterValues);
    this.drawerService.setPersistentState(DRAWER_IDS.TEMPLATE_LOGS_FILTER, {
      appliedFilters: filterValues,
      appliedFilterLength: appliedFilterLength,
    });
    const drawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.TEMPLATE_LOGS_FILTER
    );
    drawerRef?.close({ filterValues });
  }

  private calculateAppliedFilterLength(values: FilterFormValues): number {
    return Object.values(values).filter(
      value => value !== null && value !== undefined && value !== ''
    ).length;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
